import { useCallback, useEffect, useState } from "react";

/* Hook React per integrare OneSignal Web Push SDK v16.
   Lo SDK è caricato via CDN in public/index.html con OneSignalDeferred.
   Questo hook si limita a interagire con la coda OneSignalDeferred. */

const detectWebPushSupport = () => {
    if (typeof window === "undefined") return false;
    return (
        "Notification" in window &&
        "serviceWorker" in navigator &&
        "PushManager" in window
    );
};

const detectIosLike = () => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS/.test(ua);
    return isIOS && isSafari;
};

/* Esegue una funzione una volta che lo SDK OneSignal è pronto */
const withSdk = (fn) =>
    new Promise((resolve, reject) => {
        if (typeof window === "undefined") return resolve();
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (OneSignal) => {
            try {
                const result = await fn(OneSignal);
                resolve(result);
            } catch (err) {
                reject(err);
            }
        });
    });

export const useOneSignal = () => {
    const [state, setState] = useState({
        isSupported: detectWebPushSupport(),
        isIosLike: detectIosLike(),
        permission:
            typeof Notification !== "undefined" ? Notification.permission : "default",
        isSubscribed: false,
        subscriptionId: null,
        ready: false,
    });

    const refreshState = useCallback(async () => {
        if (!detectWebPushSupport()) {
            setState((prev) => ({ ...prev, isSupported: false, ready: true }));
            return;
        }
        try {
            await withSdk((OneSignal) => {
                const subId = OneSignal?.User?.PushSubscription?.id || null;
                const optedIn = OneSignal?.User?.PushSubscription?.optedIn === true;
                const permission =
                    typeof Notification !== "undefined"
                        ? Notification.permission
                        : "default";
                setState((prev) => ({
                    ...prev,
                    permission,
                    subscriptionId: subId,
                    isSubscribed: permission === "granted" && !!subId && optedIn,
                    ready: true,
                }));
            });
        } catch (_) {
            setState((prev) => ({ ...prev, ready: true }));
        }
    }, []);

    useEffect(() => {
        refreshState();
    }, [refreshState]);

    /* Richiede permesso e iscrive l'utente alle notifiche push */
    const subscribe = useCallback(async () => {
        if (!detectWebPushSupport()) {
            throw new Error("Web Push non supportato in questo browser");
        }
        await withSdk(async (OneSignal) => {
            // v16: OneSignal.Notifications.requestPermission() apre il prompt nativo
            if (OneSignal?.Notifications?.requestPermission) {
                await OneSignal.Notifications.requestPermission();
            }
            // Forza opt-in (utile se l'utente aveva fatto opt-out in precedenza)
            if (OneSignal?.User?.PushSubscription?.optIn) {
                try {
                    await OneSignal.User.PushSubscription.optIn();
                } catch (_) {
                    /* ignora se non disponibile */
                }
            }
        });
        await refreshState();
    }, [refreshState]);

    /* Disiscrive l'utente */
    const unsubscribe = useCallback(async () => {
        await withSdk(async (OneSignal) => {
            if (OneSignal?.User?.PushSubscription?.optOut) {
                await OneSignal.User.PushSubscription.optOut();
            }
        });
        await refreshState();
    }, [refreshState]);

    /* Imposta tag chiave/valore per la segmentazione (es. offerte/servizi) */
    const setTags = useCallback(async (tags) => {
        await withSdk(async (OneSignal) => {
            const stringTags = Object.fromEntries(
                Object.entries(tags).map(([k, v]) => [
                    k,
                    typeof v === "boolean" ? (v ? "true" : "false") : String(v),
                ])
            );
            if (OneSignal?.User?.addTags) {
                await OneSignal.User.addTags(stringTags);
            }
        });
    }, []);

    return { state, subscribe, unsubscribe, setTags, refreshState };
};
